/**
 * Hilfe: `/hilfe`, `/hilfe/<topic>`, inside a club `/v/<slug>/hilfe[/<topic>]`.
 *
 * The content is @fsv/data's help, compiled into the bundle: no request, so
 * it works offline at the airfield and in the demo. The reader's device
 * section comes first (user agent + display-mode: standalone), the topics of
 * their role in the active club next; every other device and topic stays one
 * tap away. Device-specific paragraphs of a topic show for this device, with
 * a switch to see the ones for the app too.
 */
import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  allNews,
  blocksFor,
  deviceTopics,
  devicesInOrder,
  findHelpTopic,
  HELP_GROUPS,
  resolveHelpText,
  searchHelp,
  topicsForRole,
  topicsInGroup,
  tx,
  webHelpContext,
  type HelpBlock,
  type HelpContext,
  type HelpDevice,
  type HelpTopic,
} from '@fsv/data';
import { formatDate } from '@fsv/data';
import { Icon } from '../components/Icon';
import { useOptionalTenant } from '../data/hooks';
import { useTour } from '../tour/TourProvider';
import { helpPath } from './HelpLink';
import '../news/news.css';
import './help.css';

/** This browser, for "Für dein Gerät". */
export function browserHelpContext(): HelpContext {
  let standalone = false;
  try {
    standalone =
      window.matchMedia?.('(display-mode: standalone)').matches ||
      (navigator as { standalone?: boolean }).standalone === true;
  } catch {
    /* no matchMedia: a tab */
  }
  return webHelpContext(navigator.userAgent, standalone, navigator.maxTouchPoints ?? 0);
}

const text = (t: string) => resolveHelpText(t);

function Blocks({
  blocks,
  device,
  slug,
}: {
  blocks: readonly HelpBlock[];
  device: HelpDevice | null;
  slug: string | null;
}) {
  return (
    <>
      {blocksFor(blocks, device).map((b, i) => {
        if ('p' in b) return <p key={i}>{text(b.p)}</p>;
        if ('h' in b) return <h3 key={i}>{text(b.h)}</h3>;
        if ('note' in b)
          return (
            <p key={i} className="notice help__note">
              <Icon name="alert" size={18} />
              <span>{text(b.note)}</span>
            </p>
          );
        if ('steps' in b)
          return (
            <ol key={i} className="help__steps">
              {b.steps.map((s, j) => (
                <li key={j}>{text(s)}</li>
              ))}
            </ol>
          );
        if ('list' in b)
          return (
            <ul key={i} className="help__list">
              {b.list.map((s, j) => (
                <li key={j}>{text(s)}</li>
              ))}
            </ul>
          );
        return (
          <div key={i} className="help__device-block">
            <span className="help__only label">
              {tx.help.onlyOn(b.on.map((d) => tx.help.device[d]).join(', '))}
            </span>
            <Blocks blocks={b.blocks} device={null} slug={slug} />
          </div>
        );
      })}
    </>
  );
}

function TopicLink({ topic, slug }: { topic: HelpTopic; slug: string | null }) {
  return (
    <li>
      <Link to={helpPath(slug, topic.id)} className="help__topic-link">
        <span className="help__topic-title">{text(topic.title)}</span>
        <span className="muted help__topic-summary">{text(topic.summary)}</span>
        <Icon name="forward" />
      </Link>
    </li>
  );
}

function TourButton() {
  const tour = useTour();
  if (!tour.available) return null;
  return (
    <section className="panel help__tour">
      <p>{tx.help.tourHint}</p>
      <button type="button" className="btn btn--primary" onClick={tour.start}>
        <Icon name="flag" />
        {tx.help.tourStart}
      </button>
    </section>
  );
}

function NewsList() {
  return (
    <ul className="news-list">
      {allNews().map((n) => (
        <li key={n.id} className="panel">
          <strong>{n.title}</strong> <span className="muted mono">{formatDate(n.date)}</span>
          <p>{n.body}</p>
        </li>
      ))}
    </ul>
  );
}

function TopicView({
  topic,
  slug,
  context,
}: {
  topic: HelpTopic;
  slug: string | null;
  context: HelpContext;
}) {
  const [allDevices, setAllDevices] = useState(false);
  const hasDeviceBlocks = topic.blocks.some((b) => 'on' in b);
  return (
    <article className="help__topic">
      <Link to={helpPath(slug)} className="btn btn--ghost help__back">
        <Icon name="back" />
        {tx.help.back}
      </Link>
      <h1>{text(topic.title)}</h1>
      <p className="help__summary">{text(topic.summary)}</p>
      <Blocks blocks={topic.blocks} device={allDevices ? null : context.device} slug={slug} />
      {topic.id === 'demo' && <TourButton />}
      {topic.id === 'neuigkeiten' && <NewsList />}
      {hasDeviceBlocks && (
        <button
          type="button"
          className="btn btn--ghost help__toggle"
          onClick={() => setAllDevices((v) => !v)}
          aria-pressed={allDevices}
        >
          {allDevices ? tx.help.thisDevice : tx.help.allDevices}
        </button>
      )}
      {topic.see && topic.see.length > 0 && (
        <nav aria-label={tx.help.see} className="help__see">
          <h2>{tx.help.see}</h2>
          <ul className="help__topics">
            {topic.see
              .map((id) => findHelpTopic(id))
              .filter((t): t is HelpTopic => t !== null)
              .map((t) => (
                <TopicLink key={t.id} topic={t} slug={slug} />
              ))}
          </ul>
        </nav>
      )}
    </article>
  );
}

export function HelpScreen() {
  const { topic: topicId } = useParams();
  const tenant = useOptionalTenant();
  const slug = tenant?.slug ?? null;
  const context = useMemo(() => browserHelpContext(), []);
  const [query, setQuery] = useState('');

  if (topicId) {
    const topic = findHelpTopic(topicId);
    return (
      <div className="page page--narrow help">
        {topic ? (
          <TopicView topic={topic} slug={slug} context={context} />
        ) : (
          <>
            <p className="empty">{tx.help.notFound}</p>
            <Link to={helpPath(slug)} className="btn">
              {tx.help.back}
            </Link>
          </>
        )}
      </div>
    );
  }

  const results = query.trim().length >= 2 ? searchHelp(query) : null;
  const devices = deviceTopics(devicesInOrder(context));
  const role =
    tenant?.role === 'admin' || tenant?.role === 'editor' || tenant?.role === 'member'
      ? tenant.role
      : null;

  return (
    <div className="page page--narrow help">
      <header className="page__head">
        <h1>{tx.help.title}</h1>
        <p className="muted">{tx.help.intro}</p>
      </header>
      <label className="field">
        <span className="field__label">{tx.help.search}</span>
        <input
          className="input"
          type="search"
          value={query}
          placeholder={tx.help.searchPlaceholder}
          onChange={(e) => setQuery(e.target.value)}
          data-tour="help-search"
        />
      </label>
      {results ? (
        results.length === 0 ? (
          <p className="empty">{tx.help.searchEmpty}</p>
        ) : (
          <ul className="help__topics">
            {results.map((t) => (
              <TopicLink key={t.id} topic={t} slug={slug} />
            ))}
          </ul>
        )
      ) : (
        <>
          <TourButton />
          <section aria-labelledby="help-device">
            <h2 id="help-device">{tx.help.yourDevice}</h2>
            <ul className="help__topics">
              {devices[0] && <TopicLink topic={devices[0]} slug={slug} />}
            </ul>
          </section>
          {role && (
            <section aria-labelledby="help-role">
              <h2 id="help-role">{tx.help.yourRole}</h2>
              <ul className="help__topics">
                {topicsForRole(role).map((t) => (
                  <TopicLink key={t.id} topic={t} slug={slug} />
                ))}
              </ul>
            </section>
          )}
          {HELP_GROUPS.map((g) => {
            const topics = g === 'devices' ? devices.slice(1) : topicsInGroup(g);
            return (
              <section key={g} aria-labelledby={`help-${g}`}>
                <h2 id={`help-${g}`}>
                  {g === 'devices' ? tx.help.otherDevices : tx.help.groups[g]}
                </h2>
                <ul className="help__topics">
                  {topics.map((t) => (
                    <TopicLink key={t.id} topic={t} slug={slug} />
                  ))}
                </ul>
              </section>
            );
          })}
        </>
      )}
    </div>
  );
}
